import React from 'react';
import { css } from '@emotion/react';
import TopNav from '@/components/ui/TopNav/TopNav';
import EmailCard from '@/components/ui/EmailCard/EmailCard';

const page = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f9f9f6;
`;

const content = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  padding: 56px 0;
  flex: 1;
  overflow: auto;

  @media (max-width: 1169px) {
    padding: 48px 16px;
  }

  @media (max-width: 767px) {
    gap: 40px;
  }
`;

const header = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 1140px;
  max-width: 100%;

  @media (max-width: 1169px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 24px;
    align-items: stretch;
  }
`;

const tabs = css`
  display: flex;
  gap: 24px;
  align-items: baseline;

  @media (max-width: 767px) {
    justify-content: center;
    gap: 16px;
  }
`;

const tabActive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.63px;
  color: #10100f;
  white-space: nowrap;
  cursor: pointer;
  margin: 0;

  @media (max-width: 767px) {
    font-size: 18px;
    line-height: 23.2px;
  }
`;

const tabInactive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.63px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  margin: 0;
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: rgba(61, 46, 41, 0.72);
  }

  @media (max-width: 767px) {
    font-size: 18px;
    line-height: 23.2px;
  }
`;

const actions = css`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    justify-content: space-between;
  }
`;

const searchSortStatus = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const textButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 8px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content, #10100f);
  white-space: nowrap;
  transition: background var(--grn-transition-fast, 200ms) ease;

  svg {
    color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  }

  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }
`;

const iconToggleGroup = css`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const iconToggle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  transition: background var(--grn-transition-fast, 200ms) ease;

  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }
`;

const iconToggleActive = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  color: #10100f;
  background: rgba(58, 58, 44, 0.08);
`;

const newEmailButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--grn-space-s, 8px);
  height: var(--grn-control-m, 40px);
  padding: 0 var(--grn-space-m, 16px);
  background: var(--grn-color-shade12, #10100f);
  border: 1px solid transparent;
  border-radius: var(--grn-radius-s, 6px);
  cursor: pointer;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: var(--grn-weight-medium, 500);
  font-size: var(--grn-text-m, 14px);
  line-height: 20px;
  color: var(--grn-color-shade2, #f9f9f6);
  white-space: nowrap;
  transition: background var(--grn-transition-fast, 200ms) ease;

  svg {
    color: var(--grn-color-shade6);
  }

  &:hover {
    background: var(--grn-color-shade11, #2a2a28);
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const sortButton = css`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    padding: 0 8px;
    border-radius: 6px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: 'FlodeskSans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: var(--grn-color-content, #10100f);
    white-space: nowrap;
    transition: background var(--grn-transition-fast, 200ms) ease;

    svg {
      color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
    }

    &:hover {
      background: rgba(58, 58, 44, 0.06);
    }
  }
`;

const emailGrid = css`
  display: flex;
  flex-wrap: wrap;
  gap: 56px;
  width: 1140px;
  max-width: 100%;

  @media (max-width: 1169px) {
    width: 100%;
    gap: 40px;
  }
`;

const emails = [
  {
    title: 'Personal welcome message',
    image: '/images/emails/email-1.png',
    statusText: 'Draft last edited 2 hours ago',
  },
  {
    title: 'Personal welcome message',
    image: '/images/emails/email-2.png',
    statusText: 'Draft last edited 2 hours ago',
  },
  {
    title: 'Personal welcome message',
    image: '/images/emails/email-3.png',
    statusText: 'Draft last edited 2 hours ago',
  },
  {
    title: 'Personal welcome message',
    image: '/images/emails/email-4.png',
    statusText: 'Draft last edited 2 hours ago',
  },
  {
    title: 'Personal welcome message',
    image: '/images/emails/email-5.png',
    statusText: 'Draft last edited 2 hours ago',
  },
  {
    title: 'Personal welcome message',
    image: '/images/emails/email-6.png',
    statusText: 'Draft last edited 2 hours ago',
  },
];

export default function Emails() {
  return (
    <div css={page}>
      <TopNav />

      <div css={content}>
        <div css={header}>
          <div css={tabs}>
            <p css={tabActive}>My emails</p>
            <p css={tabInactive}>Folders</p>
          </div>

          <div css={actions}>
            <div css={searchSortStatus}>
              <button css={textButton} type="button">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M12 12L16.5 16.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Search
              </button>
              <button css={sortButton} type="button">
                Sort
                <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                  <path
                    d="M1 7L5 11L9 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button css={textButton} type="button">
                Status
                <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                  <path
                    d="M1 7L5 11L9 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div css={iconToggleGroup}>
              {/* Square */}
              <button css={iconToggleActive} type="button" aria-label="Grid view">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect
                    x="1"
                    y="1"
                    width="16"
                    height="16"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                </svg>
              </button>
              {/* List */}
              <button css={iconToggle} type="button" aria-label="List view">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <line
                    x1="1"
                    y1="5"
                    x2="17"
                    y2="5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <line
                    x1="1"
                    y1="9"
                    x2="17"
                    y2="9"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <line
                    x1="1"
                    y1="13"
                    x2="17"
                    y2="13"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              {/* Calendar */}
              <button css={iconToggle} type="button" aria-label="Calendar view">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect
                    x="1"
                    y="2.5"
                    width="16"
                    height="14"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                  <line x1="1" y1="7" x2="17" y2="7" stroke="currentColor" strokeWidth="1.4" />
                  <line
                    x1="5.5"
                    y1="1"
                    x2="5.5"
                    y2="4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <line
                    x1="12.5"
                    y1="1"
                    x2="12.5"
                    y2="4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <button css={newEmailButton} type="button">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M7 1V13M1 7H13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              New email
            </button>
          </div>
        </div>

        <div css={emailGrid}>
          {emails.map(email => (
            <EmailCard
              key={email.image}
              title={email.title}
              image={email.image}
              statusText={email.statusText}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
